import { cn } from '@/lib/helpers/utils';
import isEmpty from 'lodash/isEmpty';
import { Star } from 'lucide-react';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import getUserAction from '../../app/redux/actions/getUserAction';
import patchUserAction from '../../app/redux/actions/patchUserAction';
import { useAppSelector } from '../../app/redux/hooks';
import { NotificationToastBookmarkEntityMessages } from '../layout/notification-toast/NotificationToastBookmarkEntityMessages';
import Bookmark, { BookmarkProps } from './Bookmark';

interface FollowProps extends Partial<BookmarkProps> {
  getUser: any;
  patchUser: any;
}

/**
 * @return {JSX}
 */
const Follow = React.forwardRef<HTMLButtonElement, FollowProps>(
  (
    {
      id,
      bookmarkBtnId,
      getUser,
      patchUser,
      showToast,
      trackName = 'bookmark',
      trackAbacus = 'bookmark',
      callback,
      customHtml,
      icon = false,
      size = 'xs',
      variant = 'outline',
      appearance = icon ? 'gray' : 'primary',
      className
    },
    ref
  ) => {
    const user = useAppSelector((state) => state.auth?.user ?? null);
    const isSignedIn = useAppSelector((state) => state.auth?.isSignedIn ?? false);
    const bookmarkLabel = 'Follow';
    const bookmarkedLabel = 'Following';
    useEffect(() => {
      if (isEmpty(user) && isSignedIn) {
        getUser();
      }
    }, []);

    // As this component is always used by orgs and providers i've only statically set one message
    // This is due to laziness, if follow is used by other types or the message becomes unique we would need to change this
    let message: any = '';
    if (!customHtml) {
      message = NotificationToastBookmarkEntityMessages.organisation;
    }

    return (
      <Bookmark
        ref={ref}
        id={id as string}
        bookmarkLabel={bookmarkLabel}
        bookmarkedLabel={bookmarkedLabel}
        trackName={trackName}
        trackAbacus={trackAbacus}
        showToast={showToast}
        bookmarkBtnId={bookmarkBtnId as string}
        customHtml={message}
        callback={callback}
        size={size}
        variant={variant}
        appearance={appearance}
        icon={icon}
        className={cn(
          icon
            ? 'min-w-8 min-h-8'
            : 'min-h-6 px-3 py-1 border tracking-wide text-xxs uppercase border-gray-400 bg-primary-50 hover:border',
          className
        )}
        data-cy={'button:follow'}
      >
        {(bookmarked: boolean) => {
          return (
            <>
              {bookmarked ? (
                <Star
                  fill="currentColor"
                  className={cn(icon ? 'w-4 h-4' : 'w-3 h-3', 'text-primary')}
                  strokeWidth={1.5}
                />
              ) : (
                <Star className={cn(icon ? 'w-4 h-4' : 'w-3 h-3', 'hover:text-primary')} strokeWidth={1.5} />
              )}
              <span className={icon ? 'sr-only' : ''}>{bookmarked ? bookmarkedLabel : bookmarkLabel}</span>
            </>
          );
        }}
      </Bookmark>
    );
  }
);

const mapDispatchToProps = {
  getUser: getUserAction,
  patchUser: patchUserAction
};

export default connect(null, mapDispatchToProps, null, { forwardRef: true })(Follow);
